import { noop } from 'lodash';

import {
   NoAnswersProvidedFault,
   SectionAnswersSaveCommand,
   SurveyNotUpdatableFault,
} from '@dreryk/egabinet-survey-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { useTranslate } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { MEDICAL_PROFILE_TEMPLATE_CODE_LIST } from '../../../common/medicalProfile/medicalProfileTemplateCodes';
import { appointmentsTranslationKeys } from '../../../translations';
import { SectionSavePayload } from '../MedicalProfileModal.types';

import { PersistableSectionSavePayload } from './MedicalProfileSaveAction.types';
import {
   aggregateSaveResults,
   describeSaveFailure,
   getRejectedResults,
   SurveyMissingError,
} from './MedicalProfileSaveAction.utils';

type MedicalProfileSaveActionRenderProps = {
   readonly saveSections: (payloads: SectionSavePayload[]) => Promise<void>;
};

type MedicalProfileSaveActionProps = {
   readonly onSuccess: VoidFunction;
   readonly children: (renderProps: MedicalProfileSaveActionRenderProps) => JSX.Element;
};

export const MedicalProfileSaveAction = (props: MedicalProfileSaveActionProps): JSX.Element => {
   const translate = useTranslate();

   const { executeAsync: saveSection } = useCqrsCommand<
      void,
      PersistableSectionSavePayload,
      SurveyNotUpdatableFault | NoAnswersProvidedFault
   >(
      // TODO DREGAB-29796: provisional SectionAnswersSaveCommand mapping, pending backend confirmation
      (parameters) => {
         const command = new SectionAnswersSaveCommand();
         command.surveyId = parameters.surveyId;
         command.sectionId = parameters.sectionId;
         command.answers = parameters.answers;
         command.recordId = Slot.emptyIfNull(parameters.recordId ?? null);
         // TODO DREGAB-29796: carry parameters.showOnVisit once SectionAnswersSaveCommand exposes the field (Show_On_Visit_Seam)
         return command;
      },
      {
         onFault: {
            'egabinet-survey-frontend-api:SurveyNotUpdatableFault': noop,
            'egabinet-survey-frontend-api:NoAnswersProvidedFault': noop,
         },
         onUnexpectedError: noop,
      }
   );

   const resolveTabLabel = (templateCode: string): string => {
      const entry = MEDICAL_PROFILE_TEMPLATE_CODE_LIST.find((candidate) => candidate.templateCode === templateCode);
      return entry ? translate(entry.tabLabel) : templateCode;
   };

   const saveSections = async (payloads: SectionSavePayload[]): Promise<void> => {
      if (payloads.length === 0) {
         props.onSuccess();
         return;
      }

      const settledResults = await Promise.allSettled(
         payloads.map(async (payload) => {
            if (payload.surveyId === null) {
               throw new SurveyMissingError();
            }
            return saveSection({ ...payload, surveyId: payload.surveyId });
         })
      );
      const rejectedResults = getRejectedResults(aggregateSaveResults(payloads, settledResults));

      if (rejectedResults.length > 0) {
         dispatchSnackbarNegativeEvent(describeSaveFailure(rejectedResults, resolveTabLabel));
         return;
      }

      dispatchSnackbarPositiveEvent(appointmentsTranslationKeys.medicalProfile.save.success);
      props.onSuccess();
   };

   return props.children({ saveSections: saveSections });
};
